import { Outlet } from "react-router";
import { OrganisationFeatureFlags, ACCOUNTING_CONFIG } from "shared";
import {
  AdjustmentsHorizontalIcon,
  BanknotesIcon,
  BoltIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  DocumentTextIcon,
  KeyIcon,
  LinkIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";

import { Card } from "../../../../../common/Atoms/Card";
import { CardContent } from "../../../../../common/Atoms/CardContent";
import { ITab, Tabs } from "../../../../../common/Atoms/Tabs";
import { useGeneralSettingsForm } from "../../../../hooks/useGeneralSettingsForm";
import { useGetSelectedOrganisation } from "../../../../hooks/useGetSelectedOrganisation";
import { useAllGroupRules } from "../../../../hooks/useGroups";
import { SaveAndError } from "../SaveAndError";
import { ConnectionSettings } from "../../ConnectionSettings";
import { useAccountingSystemSetting } from '../../../../hooks/useAccountingSystemSetting';

import { DefaultBehaviour } from "./DefaultBehaviour";
import { GroupDefaultSettings } from "./GroupAdvanced/GroupDefaultSettings";
import { PaymentPlanGlobalSettings } from "./PaymentPlanGlobalSettings";
import { Webhooks } from "./Webhooks";
import { ApiTokens } from "./ApiTokens";
import { ApiRequestLog } from "./ApiRequestLog";
import { AlternativeInvoicePdf } from "./AlternativeInvoicePdf";
import { ScheduledExports } from "./ScheduledExports";
import { CreditScoreConfig } from "./CreditScoreConfig";

/** Layout for the advanced settings - each section is a routed sub-tab rendered into the outlet */
export function AdvancedRoot() {
  const currentOrg = useGetSelectedOrganisation();
  const showApiTokens = !!currentOrg?.featureFlags?.includes(OrganisationFeatureFlags.PUBLIC_API);
  const showInvoicePdf = useAccountingSystemSetting(ACCOUNTING_CONFIG.SUPPORTS_ALTERNATIVE_INVOICE_PDF);

  // Legacy feature - GroupDefaultSettings renders nothing without existing rules, so hide its tab too
  const showAutoAssignRules = useAllGroupRules().length > 0;

  const tabs: ITab[] = [
    {
      name: `Default Behaviour`,
      href: ``,
      icon: AdjustmentsHorizontalIcon,
    },
    ...(showAutoAssignRules ? [{
      name: `Auto-Assign Rules`,
      href: `auto-assign`,
      icon: UserGroupIcon,
    }] : []),
    {
      name: `Webhooks`,
      href: `webhooks`,
      icon: BoltIcon,
    },
    {
      name: `Payment Plans`,
      href: `payment-plans`,
      icon: BanknotesIcon,
    },
    ...(showInvoicePdf ? [{
      name: `Invoice PDF`,
      href: `invoice-pdf`,
      icon: DocumentTextIcon,
    }] : []),
    {
      name: `Scheduled Exports`,
      href: `scheduled-exports`,
      icon: CalendarDaysIcon,
    },
    ...(showApiTokens ? [{
      name: `API & MCP`,
      href: `api-tokens`,
      icon: KeyIcon,
    }] : []),
    {
      name: `Credit Score`,
      href: `credit-score`,
      icon: ChartBarIcon,
    },
    {
      name: `Connection`,
      href: `connection`,
      icon: LinkIcon,
    },
  ];

  return (
    <Card>
      <CardContent className={ `pt-6` }>
        <Tabs
          tabs={ tabs }
          small
        />

        <Outlet />
      </CardContent>
    </Card>
  );
}

export function AdvancedDefaultBehaviour() {
  const { formData, onChange } = useGeneralSettingsForm();

  return (
    <>
      <DefaultBehaviour
        formData={ formData }
        onChange={ onChange }
      />

      <SaveAndError />
    </>
  );
}

export function AdvancedAutoAssignRules() {
  return (
    <GroupDefaultSettings />
  );
}

export function AdvancedWebhooks() {
  const { formData, onChange, onChangeMultiple } = useGeneralSettingsForm();

  return (
    <>
      <Webhooks
        formData={ formData }
        onChange={ onChange }
        onChangeMultiple={ onChangeMultiple }
      />

      <SaveAndError />
    </>
  );
}

export function AdvancedPaymentPlans() {
  const { formData, onChange } = useGeneralSettingsForm();

  return (
    <>
      <PaymentPlanGlobalSettings
        formData={ formData }
        onChange={ onChange }
      />

      <SaveAndError />
    </>
  );
}

export function AdvancedInvoicePdf() {
  const { formData, onChange } = useGeneralSettingsForm();
  const supported = useAccountingSystemSetting(ACCOUNTING_CONFIG.SUPPORTS_ALTERNATIVE_INVOICE_PDF);

  if (!supported) {
    return null;
  }

  return (
    <>
      <AlternativeInvoicePdf
        formData={ formData }
        onChange={ onChange }
      />

      <SaveAndError />
    </>
  );
}

export function AdvancedApiTokens() {
  const currentOrg = useGetSelectedOrganisation();

  if (!currentOrg?.featureFlags?.includes(OrganisationFeatureFlags.PUBLIC_API)) {
    return null;
  }

  return (
    <>
      <ApiTokens />
      <ApiRequestLog />
    </>
  );
}

export function AdvancedCreditScore() {
  return (
    <CreditScoreConfig />
  );
}

export function AdvancedScheduledExports() {
  return (
    <ScheduledExports />
  );
}

export function AdvancedConnection() {
  return (
    <ConnectionSettings />
  );
}
